define((require) => {
    "use strict";

    const $                 = require("jquery");
    const Class             = require("shared/util/Class");
    const HttpStatusCodes   = require("shared/util/HttpStatusCodes");
    const NotificationBar   = require("editor/ui/notificationbar/NotificationBar");
    const RestApi           = require("shared/services/RestApi");
    const Time              = require("shared/services/Time");
    const LoggedIn          = require("shared/security/LoggedIn");
    const LoggedInUserState = require("shared/ui/user/LoggedInUserState");
    const {DateUtil}          = require("shared/util/DateUtil");
    const Preconditions     = require("shared/util/Preconditions");
    const Permissions       = require("shared/security/Permissions");
    const Promise           = require("shared/util/Promise");
    const UrlUtil           = require("shared/util/UrlUtil");

    const TAG = "confirm_identity";

    // Used to ensure the user is notified about the need for confirmation every "max" location change
    // when unconfirmed...
    const NORMAL_MAX = 6;
    const SENT_MAX = 18;
    let max = NORMAL_MAX;
    let count = max - 1; // to notify immediately upon first location change...

    // True when a request to confirm is pending, false otherwise...
    let confirming = false;

    /**
     * Returns a new set of options to use for notifications related to user confirmation.
     *
     * @param  {String} tag The postfix for the tag to use in the options. The tag prefix is always
     *                      <tt>confirm_identity</tt>.
     * @return {*} A new set of options to use; never null.
     */
    function options(tag) {
        return {
            tag:              `${TAG}_${tag}`,
            ignoreDuplicates: true,
            flash:            {duplicate: true},
            clearOnClose:     "tag",
            closeDelay:       tag === "error" ? 30000 : 15000,
            queue:            false,
            onClose() { count = 0; },
            events: {
                "click .resend-confirmation": function(event) {
                    event.preventDefault();
                    manager.requestConfirmationInfo(true);
                }
            }
        };
    }

    /**
     * Returns true if the user account is already confirmed for the user associated with the logged in principal,
     * false otherwise. <p>
     *
     * If said user is already confirmed, a notification message is shown if <tt>notify</tt> is true.
     *
     * @param {Boolean} [notify] True to show a notification on already confirmed where applicable, false otherwise.
     * @return {Boolean} True if already confirmed, false otherwise.
     */
    function checkConfirmed(notify) {
        const user = LoggedIn.user();
        const date = user && user.get("confirmedAt");

        if (date) {
            if (notify) {
                NotificationBar.open(`Your account for ${user.get("username")} was already confirmed on ${DateUtil.formatMMDDYYY(date)}.`, options("already_confirmed"));
            }
            return true;
        }

        return false;
    }

    /** Shows a generic error message for unknown confirmation error. */
    function unknownError() {
        const user = LoggedIn.user();
        const userName = user ? ` for ${user.get("username")}` : "";
        NotificationBar.error({
            message: `There was an unknown error confirming your account${userName}. Please try again later.`,
            action: "support community"
        }, options("error"));
    }

    /** Closes all notification messages about confirmation, if any. */
    function close() {
        NotificationBar.close({tag: `${TAG}:`}); // any such prefixed tag
    }

    /**
     * Handles all confirmation logic with regards to users confirming their identity only. <p>
     *
     * It will present info-bars to the user when needed and it will interact with the
     * API to handle the confirmation process.
     *
     * @author Jacob Midtgaard-Olesen / <a href="http://www.zmags.com">Zmags</a>
     * @author Gunni Rode / <a href="http://www.zmags.com">Zmags</a>
     */
    const ConfirmationManager = Class.extend({

        /**
         * Attempt to confirm the logged in user with the given token (from a link in an email). <p>
         *
         * If the user is already confirmed, a message will be displayed saying so.
         *
         * @param {String} token The account confirm token for the user in question, supplied via a link in a email
         *                       with the token.
         * @return {Promise} A promise that will resolve when the confirmation has been made, or fail otherwise.
         */
        confirm(token) {
            Preconditions.checkArgument(token, "Missing user confirm token");

            // Already confirmed?
            if (checkConfirmed(true)) {
                return Promise.resolved();
            }

            // To avoid getting duplicate messages...
            confirming = true;

            // Hide notification messages related to confirmation, if any...
            close();

            const principal = LoggedIn.principal();
            const user = principal.get("user");
            const scope = this;

            return $.ajax({
                url: RestApi.pathToSpecificResource(RestApi.Collections.USERS, `confirm/${token}`),
                type: "GET"
            })
                .done(() => {
                // Mark user as confirmed, which will trigger a principal re-login...
                    user.set("confirmedAt", Time.server());
                    NotificationBar.open(`Your account (${user.get("username")}) is now confirmed. Thank you!`, options("confirmed"));
                })
                .fail((data) => {
                    const ops = options("error");

                    // Not found...
                    if (data.status === HttpStatusCodes.NOT_FOUND) {
                        NotificationBar.error(`Your confirmation link seems to be invalid.&nbsp;&nbsp;&nbsp;<a href='#user/requestConfirmEmail' class='resend-confirmation'>Please reconfirm your identity</a>${scope._lastSent(user)}`, ops);

                        // Gone/expired...
                    } else if (data.status === HttpStatusCodes.GONE) {
                        scope.requestConfirmationInfo(false).done(() => {
                            NotificationBar.open(`Your confirmation link has expired, but we have sent a new confirmation email to ${user.get("username")}.`, ops);
                        }).fail(unknownError);

                        // Something else...
                    } else {
                        unknownError();
                    }
                })
                .always(() => {
                    confirming = false;
                });
        },

        /**
         * Sends an email with a link for the logged in user to confirm the identity if not already confirmed.
         *
         * @param {Boolean} [notify] True to show a notification about the identity already confirmed, or on success or
         *                           failure of the sending of such a confirmation email.
         * @return {Promise} A promise that will resolve when the request confirmation has been sent, or fail otherwise.
         */
        requestConfirmationInfo(notify) {
            // Already confirming or confirmed? Then do nothing except perhaps show a notification for the user...
            if (confirming || checkConfirmed(notify)) {
                return Promise.resolved();
            }

            const user = LoggedIn.user();
            const ops =  notify ? options("request") : null;

            return $.ajax({
                url:         RestApi.pathToSpecificResource(RestApi.Collections.USERS, "confirm"),
                type:        "POST",
                data:        JSON.stringify({baseUrl: UrlUtil.baseUrl()}),
                dataType:    "json",
                contentType: "application/json"
            })
                .done(() => {
                // Set a dummy date to avoid several silent requests before user model reloaded from server (read only
                // on server model). The user can still request additional mails via the settings menu...
                    user.set("lastConfirmationEmailSent", Time.server());
                    // The user just requested confirmation, so stop bugging her a while, since we assume she will confirm
                    // very soon in the current session...
                    if (notify) {
                        max = SENT_MAX;
                        NotificationBar.open(`A confirmation email has been sent to ${user.get("username")}. Please follow the instructions in it to confirm your identity.`, ops);
                    } else {
                        close();
                    }
                })
                .fail(() => {
                    max = NORMAL_MAX;

                    if (notify) {
                        NotificationBar.open({message: `Sorry, but we could not send a confirmation email to ${user.get("username")} at this time. Please try again later.`, action: "support community"}, options("error"));
                    } else {
                        close();
                    }
                });
        },

        /**
         * @private
         * Returns a user friendly string of the date a confirmation mail was last sent.
         *
         * @param {User} user The unconfirmed user; never null.
         * @return {String} The user friendly message, or empty if no mail ever sent.
         */
        _lastSent(user) {
            const lastSent = user.get("lastConfirmationEmailSent");
            if (lastSent) {
                return ` - last email sent ${DateUtil.formatMMDDYYY(lastSent)}`;
            }

            return "";
        },

        /**
         * Returns the specified user, defaulting to the one associated with the currently logged in principal,
         * if confirmation is required, false otherwise. <p>
         *
         * We only require mandatory confirmation for any user not confirmed yet <b>and</b> where the role is higher
         * than Spectator; the default role is Spectator anyways, so the user is free to confirm her identity (via
         * the Settings menu), but we will not force her to (until the role is increased).
         *
         * @param {User} [user] The user to check; will default to the user associated with the currently logged in
         *                      principal, if any.
         * @return {User} The (defaulted) user if confirmation is required, null otherwise.
         */
        confirmationRequired(user = LoggedIn.user()) {
            const principal = LoggedIn.principal();

            // No confirmation is required if we're logged in through the AD directory
            if (principal && principal.get("type") === "AD") {
                return null;
            }

            return (user && !user.isConfirmed() && Permissions.Roles.SPECTATOR.not(user)) ? user : null;
        }

    });

    // Singleton...
    let manager = new ConfirmationManager();

    // Make sure the user is notified every 6th time the location changes if unconfirmed...
    LoggedInUserState.on("change:location", () => {
        const user = !confirming && manager.confirmationRequired();

        if (user) {
            // Can actually be larger than "max" if notification is shown, and the user changes location
            // drastically, but "count" will not be reset until such a notification closes...
            if ((++count % max) === 0) {
                const ops = options("confirm");
                ops.flash.duplicate = false;
                NotificationBar.open(`You are currently in Spectator mode, and you need to confirm your identity (${user.get("username")})
                     in order to use your ${user.get("role").name}
                     role.&nbsp;&nbsp;&nbsp;<a href='#user/requestConfirmEmail' class='resend-confirmation'>Confirm your identity</a>${manager._lastSent(user)}`, ops);

                max = NORMAL_MAX;
            }
        } else {
            max = NORMAL_MAX;
        }
    });

    return manager;
});



// WEBPACK FOOTER //
// ./src/editor/services/ConfirmationManager.js